import { useCallback, useMemo, useReducer } from 'react';

import uniq from 'lodash/uniq';

import { CompanyWorkerItem } from '@vyce/core/src/types';

import { BulkScope, SelectionState, WorkersFilter } from '../types';

type Action =
  | { type: 'addPhones'; phones: string[] }
  | { type: 'removePhone'; phone: string }
  | { type: 'toggleWorker'; worker: CompanyWorkerItem; covered: boolean }
  | { type: 'removeWorker'; userId: string }
  | { type: 'selectAll'; scope: BulkScope }
  | { type: 'clearBulk' }
  | { type: 'reset' };

const initialState: SelectionState = {
  phones: [],
  picked: new Map(),
  bulk: null,
  excluded: new Map(),
};

/** Whether every row of the current filter belongs to the bulk scope */
export const coveredByBulk = (bulk: BulkScope | null, filter: WorkersFilter): boolean => {
  if (!bulk) {
    return false;
  }
  if (bulk.kind === 'all') {
    return true;
  }
  return filter.module === bulk.module && filter.resourceId === bulk.resourceId;
};

const reducer = (state: SelectionState, action: Action): SelectionState => {
  switch (action.type) {
    case 'addPhones':
      return { ...state, phones: uniq([...state.phones, ...action.phones]) };
    case 'removePhone':
      return { ...state, phones: state.phones.filter(phone => phone !== action.phone) };
    case 'toggleWorker': {
      const { worker, covered } = action;
      const picked = new Map(state.picked);
      const excluded = new Map(state.excluded);
      if (covered) {
        // Inside the bulk scope a tick means "not excluded"; an explicit pick would be redundant
        if (excluded.has(worker.user_id)) {
          excluded.delete(worker.user_id);
        } else {
          excluded.set(worker.user_id, worker);
          picked.delete(worker.user_id);
        }
      } else if (picked.has(worker.user_id)) {
        picked.delete(worker.user_id);
      } else {
        picked.set(worker.user_id, worker);
      }
      return { ...state, picked, excluded };
    }
    case 'removeWorker': {
      const picked = new Map(state.picked);
      picked.delete(action.userId);
      return { ...state, picked };
    }
    case 'selectAll':
      // A new scope replaces the old one; exclusions belonged to the old scope
      return { ...state, bulk: action.scope, excluded: new Map() };
    case 'clearBulk':
      return { ...state, bulk: null, excluded: new Map() };
    case 'reset':
      return initialState;
    default:
      return state;
  }
};

/** Who is being invited: phone contacts, explicit picks and an optional "select all" scope. */
export const useInviteSelection = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Plural so a pasted batch is one dispatch and one uniq, not one per number
  const addPhones = useCallback((phones: string[]) => dispatch({ type: 'addPhones', phones }), []);
  const removePhone = useCallback((phone: string) => dispatch({ type: 'removePhone', phone }), []);
  const removeWorker = useCallback((userId: string) => dispatch({ type: 'removeWorker', userId }), []);
  const selectAll = useCallback((scope: BulkScope) => dispatch({ type: 'selectAll', scope }), []);
  const clearBulk = useCallback(() => dispatch({ type: 'clearBulk' }), []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  const isCovered = useCallback((filter: WorkersFilter) => coveredByBulk(state.bulk, filter), [state.bulk]);

  const toggleWorker = useCallback(
    (worker: CompanyWorkerItem, filter: WorkersFilter) =>
      dispatch({ type: 'toggleWorker', worker, covered: coveredByBulk(state.bulk, filter) }),
    [state.bulk]
  );

  const isChecked = useCallback(
    (worker: CompanyWorkerItem, filter: WorkersFilter) =>
      coveredByBulk(state.bulk, filter)
        ? !state.excluded.has(worker.user_id)
        : state.picked.has(worker.user_id),
    [state.bulk, state.excluded, state.picked]
  );

  const counts = useMemo(() => {
    const bulkRemaining = state.bulk ? Math.max(state.bulk.count - state.excluded.size, 0) : 0;
    const existing = state.picked.size + bulkRemaining;
    return {
      phones: state.phones.length,
      picked: state.picked.size,
      bulkRemaining,
      existing,
      total: state.phones.length + existing,
      // Explicit picks may overlap the bulk scope, so the total is an upper bound then
      isEstimate: !!state.bulk && state.picked.size > 0,
    };
  }, [state]);

  return {
    state,
    counts,
    addPhones,
    removePhone,
    toggleWorker,
    removeWorker,
    selectAll,
    clearBulk,
    reset,
    isCovered,
    isChecked,
  };
};

export type InviteSelectionApi = ReturnType<typeof useInviteSelection>;
